
import React, { useRef, useState, useMemo, useEffect } from "react";
import { FontSizeOutlined, HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Input, Select, Button, Space, Table, Modal, notification, Switch } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { Resizable } from 'react-resizable';
import type { ResizeCallbackData } from 'react-resizable';
import useSWR, { mutate } from 'swr';
import axios from 'axios';
import "./data_work_order.css";
import { useNavigate } from "react-router-dom";
import { getStatusLabel } from "../../components/hoc_work_order";
import { useAuth } from "../../context/AuthContext";

const fetcher = (url: string) => axios.get(url).then(res => res.data);

interface WorkOrderDataType {
    id: string;
    work_estimation_no: string;
    work_estimation_date: string;
    work_order_no: string;
    work_order_date: string;
    brand: string;
    model: string;
    chassis_no: string;
    no_polisi: string;
    status: string;
    warehouse?: string;
    warehouse_name?: string;
}

type DataIndex = keyof WorkOrderDataType;

const ResizableTitle = (props: React.HTMLAttributes<any> & { onResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void; width: number; onDoubleClick: () => void; }) => {
    const { onResize, width, onDoubleClick, ...restProps } = props;

    if (!width) {
        return <th {...restProps} onDoubleClick={onDoubleClick} />;
    }

    return (
        <Resizable
            width={width}
            height={0}
            handle={
                <span
                    className="react-resizable-handle"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                />
            }
            onResize={onResize}
            draggableOpts={{ enableUserSelectHack: false }}
        >
            <th {...restProps} onDoubleClick={onDoubleClick} />
        </Resizable>
    );
};


const DataWorkOrder: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const { data: workOrderData, error } = useSWR<WorkOrderDataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/work-order/data`, fetcher);
    const { userData } = useAuth();
    const [showAllWarehouse, setShowAllWarehouse] = useState(false);
    const navigate = useNavigate();

    const hasOtoritas77 = useMemo(() => {
        if (!userData?.otoritas) return false;
        return userData.otoritas.split(';').map((v: string) => v.trim()).includes('77');
    }, [userData]);

    const filteredWorkOrderData = useMemo(() => {
        if (!workOrderData) return [];
        if (hasOtoritas77 && showAllWarehouse) {
            return workOrderData;
        }
        return workOrderData.filter(item => item.warehouse === userData?.warehouse_code);
    }, [workOrderData, hasOtoritas77, showAllWarehouse, userData]);

    const warehouseFilters = useMemo(() => {
        const names = filteredWorkOrderData.map(item => item.warehouse_name || item.warehouse || '').filter(Boolean);
        return Array.from(new Set(names)).map(name => ({ text: name, value: name }));
    }, [filteredWorkOrderData]);
    useEffect(() => {
        setColumns(prevColumns =>
            prevColumns.map(col =>
                col.key === 'warehouse' ? { ...col, filters: warehouseFilters } : col
            )
        );
    }, [warehouseFilters]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<WorkOrderDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {

            const recordValue = record[dataIndex];
            if (recordValue === null || recordValue === undefined) {
                return false;
            }
            return String(recordValue)
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const [columns, setColumns] = useState<TableColumnsType<WorkOrderDataType>>([
        {
            title: 'Work Estimation No',
            dataIndex: 'work_estimation_no',
            key: 'work_estimation_no',
            width: 180,
            sorter: (a, b) => a.work_estimation_no.localeCompare(b.work_estimation_no),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('work_estimation_no'),
            ellipsis: true,
        },
        {
            title: 'Work Estimation Date',
            dataIndex: 'work_estimation_date',
            key: 'work_estimation_date',
            width: 150,
            sorter: (a, b) => (a.work_estimation_date || '').localeCompare(b.work_estimation_date || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('work_estimation_date'),
            ellipsis: true,
            render: (text: string) => text,
        },
        {
            title: 'Work Order No',
            dataIndex: 'work_order_no',
            key: 'work_order_no',
            width: 150,
            sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('work_order_no'),
            ellipsis: true,
        },
        {
            title: 'Work Order Date',

            dataIndex: 'work_order_date',
            key: 'work_order_date',
            width: 150,
            sorter: (a, b) => (a.work_order_date || '').localeCompare(b.work_order_date || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('work_order_date'),
            ellipsis: true,
            render: (text: string) => text,
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            width: 120,
            sorter: (a, b) => (a.brand || '').localeCompare(b.brand || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('brand'),
            ellipsis: true,
        },
        {
            title: 'Model',
            dataIndex: 'model',
            key: 'model',
            width: 120,
            sorter: (a, b) => (a.model || '').localeCompare(b.model || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('model'),
            ellipsis: true,
        },
        {
            title: 'Chassis No.',
            dataIndex: 'chassis_no',
            key: 'chassis_no',
            width: 180,
            sorter: (a, b) => a.chassis_no.localeCompare(b.chassis_no),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('chassis_no'),
            ellipsis: true,
        },
        {
            title: 'No. Pol.',
            dataIndex: 'no_polisi',
            key: 'no_polisi',
            width: 120,
            sorter: (a, b) => a.no_polisi.localeCompare(b.no_polisi),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('no_polisi'),
            ellipsis: true,
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            width: 120,
            sorter: (a, b) => (a.warehouse || '').localeCompare(b.warehouse || ''),
            sortDirections: ['descend', 'ascend'],
            filters: warehouseFilters,
            onFilter: (value, record) => (record.warehouse_name || '') === value,
            ellipsis: true,
            render: (text: string, record: WorkOrderDataType) => record.warehouse_name || text,
        },
        {
            title: 'Order Status',
            dataIndex: 'status',
            key: 'status',
            width: 180,
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortDirections: ['descend', 'ascend'],
            filters: [
                { text: 'Draft', value: '1' },
                { text: 'New', value: '2' },
                { text: 'Waiting for Approved', value: '3' },
                { text: 'Complete', value: '4' },
                { text: 'Canceled', value: '5' },
                { text: 'QC Pass', value: '6' },
                { text: 'On Going', value: '7' },
                { text: 'Wating for Approved QC Pass', value: '33' },
                { text: 'Billing Preparation', value: '9' },
                { text: 'Waiting for Approved', value: '10' },
            ],
            onFilter: (value, record) => record.status === value,
            ellipsis: true,
            render: (text: string) => getStatusLabel(text),
        },
    ]);

    const handleResize = (index: number) => (
        e: React.SyntheticEvent,
        { size }: ResizeCallbackData
    ) => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: size.width,
            };
            return newColumns;
        });
    };

    const autoSizeColumn = (dataIndex: DataIndex) => {

        if (!filteredWorkOrderData) return;

        const FONT_STYLE = "14px 'Poppins', sans-serif";
        const PADDING = 32;

        const getTextWidth = (text: string, font: string): number => {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (context) {
                context.font = font;
                return context.measureText(text).width;
            }
            return 0;
        };

        const getRenderedText = (record: WorkOrderDataType, dataIndex: DataIndex): string => {
            const value = record[dataIndex];

            if (value === null || value === undefined) return '';
            return String(value);
        };

        const headerColumn = columns.find(col => col.key === dataIndex);
        const headerText = headerColumn && headerColumn.title ? String(headerColumn.title) : '';
        const headerWidth = getTextWidth(headerText, `600 ${FONT_STYLE}`) + PADDING;

        const cellWidths = filteredWorkOrderData.map(item => {
            const cellText = getRenderedText(item, dataIndex);
            return getTextWidth(cellText, FONT_STYLE) + PADDING;
        });

        const maxContentWidth = Math.ceil(Math.max(headerWidth, ...cellWidths, 50));

        setColumns(prevColumns => {
            const index = prevColumns.findIndex(col => col.key === dataIndex);
            if (index === -1) return prevColumns;
            let newWidth = maxContentWidth;
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: newWidth,
            };
            return newColumns;
        });
    };

    const resizableColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index),
            onDoubleClick: () => autoSizeColumn(column.dataIndex as DataIndex),
        }),
    }));

    const components = {
        header: {
            cell: ResizableTitle,
        },
    };

    const tableWidth = columns.reduce((sum, col) => sum + (col.width as number || 0), 0);

    const handleRowDoubleClick = (record: WorkOrderDataType) => {
        navigate('/dashboard/work_order', { state: { weNo: record.work_estimation_no } });
    };

    return (

        <div id="i21g" style={transformStyle}>
            <div className="gjs-grid-row" id="ib5z">
                <div className="gjs-grid-column" id="istf">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Work Order</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/work_order",
                                title: "Work Order",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="i84v">
                <div className="gjs-grid-column" id="icld">
                    {hasOtoritas77 && (
                        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                            <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                        </div>
                    )}
                    <Table<WorkOrderDataType>
                        bordered
                        components={components}
                        columns={resizableColumns as any}
                        dataSource={filteredWorkOrderData}
                        rowKey="id"
                        pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "50", "100"] }}
                        scroll={{ x: tableWidth }}
                        loading={!workOrderData && !error}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                handleRowDoubleClick(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataWorkOrder;